/**
 * Contact — premium black/gold contact form with 4 enquiry topics.
 *
 *   General Support · Fisheries & Partnerships · Brand & Supplier · Report a Problem
 *
 * Posts to POST /api/contact (open endpoint; ip-throttled server-side).
 */
import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Mail,
  MessageSquare,
  MapPin,
  Award,
  AlertCircle,
  Send,
  Check,
  Loader2,
  ExternalLink,
  Instagram,
  Twitter,
  Youtube,
  Handshake,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import Footer from '../components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOPIC_CARDS = [
  {
    value: 'general_support',
    icon: MessageSquare,
    title: 'General Support',
    description: 'Account questions, technical help, or anything else not covered below.',
    email: 'support@thecarpcast.co.uk',
  },
  {
    value: 'fisheries_partnership',
    icon: MapPin,
    title: 'Fisheries & Partnerships',
    description: 'Get your fishery onto the platform, list day tickets, or discuss booking integration.',
    email: 'partnerships@thecarpcast.co.uk',
  },
  {
    value: 'brand_supplier',
    icon: Award,
    title: 'Brand & Supplier Enquiries',
    description: "Bait, tackle and gear brands — let's talk about being part of the marketplace launch.",
    email: 'partnerships@thecarpcast.co.uk',
  },
  {
    value: 'report_problem',
    icon: AlertCircle,
    title: 'Report a Problem',
    description: 'Spotted a bug, dodgy catch report or anything that needs moderating? Tell us here.',
    email: 'support@thecarpcast.co.uk',
  },
];

const SOCIAL_LINKS = [
  { icon: Instagram, label: 'Instagram', placeholder: 'Coming soon' },
  { icon: Twitter,   label: 'Twitter / X', placeholder: 'Coming soon' },
  { icon: Youtube,   label: 'YouTube',    placeholder: 'Coming soon' },
];

const Contact = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    topic: 'general_support',
    subject: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) return toast.error('Please tell us your name');
    if (!/\S+@\S+\.\S+/.test(form.email)) return toast.error('Please enter a valid email');
    if (form.message.trim().length < 10) return toast.error('Please share a few more details (10+ characters)');

    setSubmitting(true);
    try {
      const token = localStorage.getItem('carpcast_token');
      await axios.post(
        `${API}/contact`,
        {
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          topic: form.topic,
          subject: form.subject.trim() || null,
          message: form.message.trim(),
        },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      setSuccess(true);
      toast.success('Message received — we\'ll be in touch.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send — please try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050605]" data-testid="contact-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_20%,rgba(200,178,115,0.12),transparent_60%)] pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 rounded-full border border-[#c8b273]/30 bg-[#c8b273]/5 px-4 py-1.5 mb-5"
          >
            <Mail className="w-3.5 h-3.5 text-[#c8b273]" />
            <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#c8b273]">
              Get in touch
            </span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-['Barlow_Condensed'] text-5xl md:text-6xl font-extrabold uppercase tracking-wider text-white leading-[0.95] mb-4"
          >
            Talk to us.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.18 }}
            className="max-w-2xl text-base md:text-lg text-[#cfd4cf] leading-relaxed"
          >
            Whether you're an angler with a question, a fishery thinking about
            joining, or a brand looking at the upcoming marketplace — we want to
            hear from you.
          </motion.p>
        </div>
      </section>

      {/* Topic cards */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {TOPIC_CARDS.map((card, i) => (
            <motion.button
              key={card.value}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              type="button"
              onClick={() => {
                update('topic', card.value);
                document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              className={`group text-left rounded-2xl border bg-[#0b0d0b] p-7 transition-all ${
                form.topic === card.value
                  ? 'border-[#c8b273]/60 ring-1 ring-[#c8b273]/30'
                  : 'border-white/10 hover:border-[#c8b273]/30'
              }`}
              data-testid={`topic-card-${card.value}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#c8b273]/30 bg-[#c8b273]/10 text-[#c8b273] mb-4">
                    <card.icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wider text-white mb-2">
                    {card.title}
                  </h3>
                  <p className="text-sm text-[#a8b0a8] leading-relaxed mb-3">
                    {card.description}
                  </p>
                  <span
                    className="inline-flex items-center gap-1.5 text-xs text-[#c8b273] hover:underline"
                  >
                    <Mail className="w-3.5 h-3.5" />
                    {card.email}
                  </span>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </section>

      {/* Partnerships & Collaborations — dedicated business-facing channel */}
      <section
        className="max-w-6xl mx-auto px-6 md:px-12 pb-4"
        data-testid="partnerships-section"
      >
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.45 }}
          className="relative overflow-hidden rounded-2xl border border-[#c8b273]/30 bg-gradient-to-br from-[#0b0d0b] via-[#0b0d0b] to-[#1a1410] p-7 md:p-10"
        >
          <div className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-[#c8b273]/10 blur-3xl" />

          <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-3">
                <span className="h-1.5 w-1.5 rounded-full bg-[#c8b273] animate-pulse" />
                <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#c8b273]">
                  Industry channel
                </span>
              </div>
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#c8b273]/40 bg-[#c8b273]/10 text-[#c8b273] mb-4">
                <Handshake className="w-5 h-5" />
              </div>
              <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-extrabold uppercase tracking-wider text-white mb-2">
                Partnerships &amp; Collaborations
              </h2>
              <p className="text-sm md:text-base text-[#a8b0a8] leading-relaxed max-w-2xl">
                For fisheries, bait brands, tackle retailers, sponsorships,
                wholesalers, and other business enquiries — get in touch directly
                with our partnerships team.
              </p>
            </div>

            <a
              href="mailto:partnerships@thecarpcast.co.uk"
              className="inline-flex items-center gap-2.5 rounded-xl border border-[#c8b273]/40 bg-[#c8b273]/5 px-5 py-4 text-[#c8b273] hover:bg-[#c8b273]/10 hover:border-[#c8b273]/70 transition-all shrink-0"
              data-testid="partnerships-email"
            >
              <Mail className="w-4 h-4" />
              <span className="font-bold tracking-wide text-sm md:text-base">
                partnerships@thecarpcast.co.uk
              </span>
            </a>
          </div>
        </motion.div>
      </section>

      {/* Form */}
      <section
        id="contact-form"
        className="bg-[#070907] py-16 md:py-24 border-y border-white/5"
      >
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <p className="data-label text-[#c8b273] mb-2">Send us a message</p>
            <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-extrabold uppercase tracking-wider text-white">
              We read every message.
            </h2>
          </div>

          {success ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="rounded-2xl border border-[#c8b273]/40 bg-[#c8b273]/5 p-8 text-center"
              data-testid="contact-success"
            >
              <div className="inline-flex h-14 w-14 items-center justify-center rounded-full border border-[#c8b273]/40 bg-[#c8b273]/10 mb-4">
                <Check className="w-6 h-6 text-[#c8b273]" />
              </div>
              <h3 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wider text-white mb-2">
                Thanks — your message is in.
              </h3>
              <p className="text-[#a8b0a8] leading-relaxed max-w-md mx-auto mb-6">
                We aim to get back to all enquiries within two working days.
                Keep an eye on your inbox (including spam, just in case).
              </p>
              <Button
                onClick={() => {
                  setSuccess(false);
                  setForm({ name: '', email: '', topic: 'general_support', subject: '', message: '' });
                }}
                variant="ghost"
                className="text-[#c8b273] hover:bg-white/5"
                data-testid="contact-send-another"
              >
                Send another message
              </Button>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="data-label text-[#a8b0a8] mb-2 block">Your name</Label>
                  <Input
                    type="text"
                    value={form.name}
                    onChange={(e) => update('name', e.target.value)}
                    placeholder="Jane Angler"
                    autoComplete="name"
                    required
                    className="bg-[#141714] border-white/10 text-white placeholder:text-[#6b7d6b]"
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <Label className="data-label text-[#a8b0a8] mb-2 block">Email</Label>
                  <Input
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    value={form.email}
                    onChange={(e) => update('email', e.target.value)}
                    placeholder="you@email.com"
                    required
                    className="bg-[#141714] border-white/10 text-white placeholder:text-[#6b7d6b]"
                    data-testid="contact-email"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="data-label text-[#a8b0a8] mb-2 block">Topic</Label>
                  <Select value={form.topic} onValueChange={(v) => update('topic', v)}>
                    <SelectTrigger className="bg-[#141714] border-white/10 text-white" data-testid="contact-topic">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0b0d0b] border-white/10">
                      {TOPIC_CARDS.map((t) => (
                        <SelectItem key={t.value} value={t.value}>{t.title}</SelectItem>
                      ))}
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="data-label text-[#a8b0a8] mb-2 block">Subject (optional)</Label>
                  <Input
                    type="text"
                    value={form.subject}
                    onChange={(e) => update('subject', e.target.value)}
                    placeholder="A one-line summary"
                    className="bg-[#141714] border-white/10 text-white placeholder:text-[#6b7d6b]"
                    data-testid="contact-subject"
                  />
                </div>
              </div>

              <div>
                <Label className="data-label text-[#a8b0a8] mb-2 block">Message</Label>
                <Textarea
                  value={form.message}
                  onChange={(e) => update('message', e.target.value)}
                  placeholder="What's on your mind?"
                  rows={6}
                  required
                  className="bg-[#141714] border-white/10 text-white placeholder:text-[#6b7d6b]"
                  data-testid="contact-message"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="btn-gold w-full md:w-auto"
                data-testid="contact-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send message
                  </>
                )}
              </Button>
              <p className="text-xs text-[#6b7d6b]">
                We use your email only to reply to this enquiry. See our{' '}
                <a href="/privacy" className="text-[#c8b273] hover:underline">Privacy Policy</a>.
              </p>
            </form>
          )}
        </div>
      </section>

      {/* Social placeholder strip */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <p className="data-label text-[#c8b273] text-center mb-4">Follow along</p>
        <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-extrabold uppercase tracking-wider text-white text-center mb-10">
          Social channels coming soon.
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {SOCIAL_LINKS.map((s) => (
            <div
              key={s.label}
              className="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-[#0b0d0b] px-5 py-4"
              data-testid={`social-${s.label.toLowerCase().replace(/[\s/]+/g, '-')}`}
            >
              <div className="flex items-center gap-3">
                <s.icon className="w-5 h-5 text-[#c8b273]" />
                <div>
                  <p className="font-bold uppercase tracking-widest text-xs text-white">{s.label}</p>
                  <p className="text-[10px] text-[#6b7d6b]">{s.placeholder}</p>
                </div>
              </div>
              <ExternalLink className="w-4 h-4 text-[#6b7d6b]" />
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
